import { supabase } from '@/integrations/supabase/client';
import {
  applyBankRulesToNewTransactions,
  applyPOSMappingsToMenuPerformance,
} from './applyTrainedMappings';
import { convertVendorInvoicesToExpenses } from './cogsInvoiceToExpense';
import { mapPayoutToSalesTransaction } from './fileProcessors/payoutsProcessor';
import { computeAndUpsertShiftPerformance } from '@/data/supabase/shift-performance';
import { normalizePlatform } from '@/lib/platformConstants';
import {
  parseDateAndTime as parseDateAndTimeCentral,
  normalizeTimeString,
  dateToLocalDateString,
} from './dateTimeUtils';

/** Module keys used when marking onboarding sections complete */
export type OnboardingModuleKey = 'bank_accounts' | 'cogs_vendor' | 'pos_integration';

/** Result of storing mapped data for a single module */
export interface StoreMappedDataResult {
  success: boolean;
  error?: string;
}

/**
 * Parse a value that may be date-only or datetime into separate date and time.
 * Delegates to the central dateTimeUtils for proper timezone-safe parsing.
 */
function parseDateAndTime(
  value: string | undefined,
  fallbackDate?: string
): { date: string; time: string | null } {
  const fallback = fallbackDate || dateToLocalDateString(new Date());
  if (!value) return { date: fallback, time: null };
  const parsed = parseDateAndTimeCentral(value);
  return {
    date: parsed.date ?? fallback,
    time: parsed.time,
  };
}

/**
 * Sync item quantities from pos_report_uploads (item_quantities) into menu_performance
 * so dashboard/Menu Performance see onboarding data. Only inserts if menu_performance
 * has no rows for this company (avoids duplicates on re-run).
 */
async function syncItemQuantitiesFromPosReports(companyId: string): Promise<void> {
  const { data: existing } = await supabase
    .from('menu_performance')
    .select('id')
    .eq('company_id', companyId)
    .limit(1);
  if (existing && existing.length > 0) {
    return; // already have quantity data (e.g. from Sales Quantities or prior sync)
  }

  const { data: reports } = await supabase
    .from('pos_report_uploads')
    .select('platform, processed_data')
    .eq('company_id', companyId)
    .eq('report_type', 'item_quantities')
    .eq('status', 'completed')
    .order('created_at', { ascending: false });

  if (!reports || reports.length === 0) return;

  // Dedupe by platform: use latest report per platform
  const byPlatform = new Map<string, { platform: string; processed_data: unknown }>();
  for (const r of reports) {
    const platform = (r.platform || '').toLowerCase();
    if (!byPlatform.has(platform)) byPlatform.set(platform, { platform, processed_data: r.processed_data });
  }

  const defaultDate = dateToLocalDateString(new Date());
  const toInsert: Array<{
    company_id: string;
    date: string;
    time: string | null;
    item_name: string;
    quantity_sold: number;
    revenue: number;
    category: string | null;
    source: string;
  }> = [];

  for (const { platform, processed_data } of byPlatform.values()) {
    const payload = processed_data as {
      raw_data?: Array<{
        date?: string;
        time?: string;
        item_name?: string;
        quantity_sold?: number;
        revenue?: number;
        platform?: string;
        category?: string | null;
      }>;
    } | null;
    const rawData = payload?.raw_data;
    if (!Array.isArray(rawData)) continue;
    const source = normalizePlatform(platform);
    for (const row of rawData) {
      const itemName = row.item_name ?? '';
      if (!itemName) continue;
      let date: string;
      let time: string | null;
      if (row.time != null && row.time !== '') {
        date = row.date || defaultDate;
        time = normalizeTimeString(row.time);
      } else {
        const parsed = parseDateAndTime(row.date, defaultDate);
        date = parsed.date;
        time = parsed.time;
      }
      toInsert.push({
        company_id: companyId,
        date,
        time,
        item_name: itemName,
        quantity_sold: Number(row.quantity_sold) || 0,
        revenue: Number(row.revenue) || 0,
        category: row.category ?? null,
        source,
      });
    }
  }

  if (toInsert.length === 0) return;

  const batchSize = 200;
  for (let i = 0; i < toInsert.length; i += batchSize) {
    const batch = toInsert.slice(i, i + batchSize);
    const { error } = await supabase.from('menu_performance').insert(batch);
    if (error) throw error;
  }
}


/** Result of syncing payouts from pos_report_uploads into sales_transactions */
export interface SyncPayoutsToSalesResult {
  inserted: number;
  skipped: number;
  errors: string[];
}

/**
 * Sync payouts from pos_report_uploads (report_type = 'payouts') into sales_transactions
 * so that data already uploaded during POS training appears in Sales Insights / dashboard.
 * Safe to run multiple times: skips rows that already exist (same company, platform, sale_date, sale_time, net_payout_amount).
 */
export async function syncPayoutsFromPosReportsToSalesTransactions(
  companyId: string
): Promise<SyncPayoutsToSalesResult> {
  const result: SyncPayoutsToSalesResult = { inserted: 0, skipped: 0, errors: [] };

  const { data: uploads, error: fetchError } = await supabase
    .from('pos_report_uploads')
    .select('id, platform, processed_data')
    .eq('company_id', companyId)
    .eq('report_type', 'payouts')
    .eq('status', 'completed')
    .order('created_at', { ascending: false });

  if (fetchError) {
    result.errors.push(fetchError.message);
    return result;
  }
  if (!uploads?.length) return result;

  // Load existing sales_transactions for this company to avoid duplicates (any source)
  const { data: existing } = await supabase
    .from('sales_transactions')
    .select('platform, sale_date, sale_time, net_payout_amount')
    .eq('company_id', companyId);

  const existingKeys = new Set(
    (existing || []).map(
      (r) =>
        `${r.platform ?? ''}|${r.sale_date ?? ''}|${r.sale_time ?? ''}|${Number(r.net_payout_amount ?? 0).toFixed(2)}`
    )
  );

  for (const upload of uploads) {
    const processedData = upload.processed_data;
    if (!processedData || !Array.isArray(processedData) || processedData.length === 0) continue;

    const toInsert: Record<string, unknown>[] = [];
    for (const p of processedData) {
      const mapped = mapPayoutToSalesTransaction(p as import('./fileProcessors/payoutsProcessor').ProcessedPayoutData);
      const row: Record<string, unknown> = {
        ...mapped,
        company_id: companyId,
        source: 'pos_training_backfill',
      };
      const key = `${row.platform ?? ''}|${row.sale_date ?? ''}|${row.sale_time ?? ''}|${Number(row.net_payout_amount ?? 0).toFixed(2)}`;
      if (existingKeys.has(key)) {
        result.skipped++;
        continue;
      }
      existingKeys.add(key);
      toInsert.push(row);
    }

    if (toInsert.length === 0) continue;

    const { error: insertError } = await supabase.from('sales_transactions').insert(toInsert as any);
    if (insertError) {
      result.errors.push(insertError.message);
      continue;
    }
    result.inserted += toInsert.length;

    computeAndUpsertShiftPerformance(companyId, toInsert as any).catch((err) =>
      console.error('shift_performance upsert after onboarding payouts sync failed:', err)
    );
  }

  return result;
}

/**
 * Stores the data that was already trained/mapped during onboarding into canonical DB
 * tables. Does NOT re-apply rules, re-run classification, or re-apply mappings.
 * Call this when a module is marked complete so the dashboard sees the onboarding data.
 *
 * - bank_accounts: set source='bank' on bank_transactions where null
 * - cogs_vendor: convert already-mapped vendor_invoice_training → bank_transactions
 * - pos_integration: sync item_quantities from pos_report_uploads → menu_performance, sync payouts → sales_transactions, then apply mappings
 */
export async function storeMappedDataForModule(
  companyId: string,
  module: OnboardingModuleKey
): Promise<StoreMappedDataResult> {
  try {
    if (module === 'bank_accounts') {
      const { error } = await supabase
        .from('bank_transactions')
        .update({ source: 'bank' })
        .eq('company_id', companyId)
        .is('source', null)
        .select('id');
      if (error) throw error;

      // Apply onboarding-trained rules to uncategorized transactions so they get
      // classified using the rules (and ML from labeled data) from training.
      try {
        await applyBankRulesToNewTransactions(companyId);
      } catch (applyErr) {
        const msg = applyErr instanceof Error ? applyErr.message : String(applyErr);
        console.warn('[storeMappedDataForModule] bank_accounts: applyBankRulesToNewTransactions failed:', msg);
        // Don't fail the whole operation; source update already succeeded.
      }

      return { success: true };
    }
    if (module === 'cogs_vendor') {
      await convertVendorInvoicesToExpenses(companyId);
      return { success: true };
    }
    if (module === 'pos_integration') {
      // Run each step independently so one failure doesn't block completion. Progress is already saved.
      // Catch-up sync on next login will retry any failed step.
      try {
        await syncItemQuantitiesFromPosReports(companyId);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.warn('[storeMappedDataForModule] pos_integration: syncItemQuantitiesFromPosReports failed:', msg);
      }
      try {
        await syncPayoutsFromPosReportsToSalesTransactions(companyId);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.warn('[storeMappedDataForModule] pos_integration: syncPayoutsFromPosReportsToSalesTransactions failed:', msg);
      }
      try {
        await applyPOSMappingsToMenuPerformance(companyId);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.warn('[storeMappedDataForModule] pos_integration: applyPOSMappingsToMenuPerformance failed:', msg);
      }
      return { success: true };
    }
    return { success: true };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(`[storeMappedDataForModule] ${module}:`, error);
    return { success: false, error: message };
  }
}

/** Sections in onboarding_progress that indicate a module is done */
const MODULE_SECTION_KEYS: Record<OnboardingModuleKey, string[]> = {
  bank_accounts: ['bank_accounts', 'phase2.bank_accounts_phase2'],
  cogs_vendor: ['phase2.cogs_vendor_training', 'vendors'],
  pos_integration: ['pos_integration', 'phase2.pos_reports'],
};

function isSectionDone(sections: Record<string, unknown>, keys: string[]): boolean {
  for (const key of keys) {
    const part = key.startsWith('phase2.') ? (sections.phase2 as Record<string, unknown>)?.[key.slice(7)] : sections[key];
    const status = (part as { status?: string })?.status;
    if (status === 'completed' || status === 'skipped') return true;
  }
  return false;
}

/** Result of the catch-up sync (so UI can show completion and invalidate cache) */
export interface CatchUpSyncResult {
  /** Modules that synced successfully */
  synced: OnboardingModuleKey[];
  /** Modules that failed with error message */
  failed: { module: OnboardingModuleKey; error: string }[];
  /** True if any onboarding modules were applicable to sync */
  hadModules: boolean;
}

/**
 * Catch-up sync (async): runs storeMappedDataForModule for each completed onboarding
 * module and returns which succeeded/failed. Use this when you need to know when sync
 * is done (e.g. show toast, invalidate dashboard queries).
 */
export async function runOnboardingDataCatchUpSyncAsync(
  companyId: string
): Promise<CatchUpSyncResult> {
  const result: CatchUpSyncResult = { synced: [], failed: [], hadModules: false };
  const { data, error } = await supabase
    .from('onboarding_progress')
    .select('sections_completed')
    .eq('company_id', companyId)
    .single();

  if (error) {
    console.error('[runOnboardingDataCatchUpSync]', error);
    return result;
  }

  const raw = (data?.sections_completed as Record<string, unknown>) || {};
  const sections = { ...raw, phase2: raw.phase2 || {} };
  const modules: OnboardingModuleKey[] = ['bank_accounts', 'cogs_vendor', 'pos_integration'];
  const toSync = modules.filter((m) => isSectionDone(sections, MODULE_SECTION_KEYS[m]));
  result.hadModules = toSync.length > 0;

  const outcomes = await Promise.allSettled(
    toSync.map(async (module) => {
      try {
        const res = await storeMappedDataForModule(companyId, module);
        return { module, ...res };
      } catch (err) {
        return { module, success: false as const, error: String(err) };
      }
    })
  );

  for (const outcome of outcomes) {
    // Each promise now always fulfills (errors caught inside), but guard anyway
    if (outcome.status !== 'fulfilled') {
      result.failed.push({
        module: toSync[0] ?? 'bank_accounts',
        error: outcome.status === 'rejected' ? String(outcome.reason) : 'Unknown error',
      });
      continue;
    }
    const { module: mod, success, error: err } = outcome.value;
    if (success) {
      result.synced.push(mod);
    } else {
      result.failed.push({ module: mod, error: err ?? 'Unknown error' });
    }
  }

  return result;
}

/**
 * Catch-up sync for users who completed onboarding before storeMappedDataForModule
 * was implemented. Call when the dashboard loads (e.g. from Index layout).
 * Fire-and-forget; for completion feedback use runOnboardingDataCatchUpSyncAsync instead.
 */
export function runOnboardingDataCatchUpSync(companyId: string): void {
  void runOnboardingDataCatchUpSyncAsync(companyId).catch((err) =>
    console.error('[runOnboardingDataCatchUpSync]', err)
  );
}

/**
 * Result of the onboarding data synchronization
 */
export interface OnboardingDataSyncResult {
  success: boolean;
  bankTransactions: {
    updated: number;
  };
  cogsInvoices: {
    total: number;
    converted: number;
    skipped: number;
    errors: number;
  };
  salesTransactions: {
    count: number;
  };
  errors: string[];
}

/**
 * Synchronizes all onboarding data to ensure it flows into financial calculations.
 * This function should be called when onboarding is completed to ensure:
 * 1. Bank transactions have the 'source' field set properly
 * 2. COGS vendor invoices are converted to expense transactions
 * 3. Sales transactions are verified as complete
 * 
 * @param companyId - The company ID to sync data for
 * @returns Sync result with details of what was processed
 */
export async function syncOnboardingDataToFinancials(
  companyId: string
): Promise<OnboardingDataSyncResult> {
  const result: OnboardingDataSyncResult = {
    success: true,
    bankTransactions: { updated: 0 },
    cogsInvoices: { total: 0, converted: 0, skipped: 0, errors: 0 },
    salesTransactions: { count: 0 },
    errors: [],
  };

  try {
    // Step 1: Ensure all bank transactions have the 'source' field set
    console.log('Step 1: Updating bank transaction sources...');
    try {
      const { data: updateResult, error: sourceError } = await supabase
        .from('bank_transactions')
        .update({ source: 'bank' })
        .eq('company_id', companyId)
        .is('source', null)
        .select('id');

      if (sourceError) {
        console.error('Error updating bank transaction sources:', sourceError);
        result.errors.push(`Bank sources update: ${sourceError.message}`);
      } else {
        result.bankTransactions.updated = updateResult?.length || 0;
        console.log(`Updated ${result.bankTransactions.updated} bank transactions with source='bank'`);
      }
    } catch (error: any) {
      console.error('Error in bank transaction source update:', error);
      result.errors.push(`Bank sources: ${error.message}`);
    }

    // Step 2: Convert COGS vendor invoices to expense transactions
    console.log('Step 2: Converting COGS invoices to expenses...');
    try {
      const cogsResult = await convertVendorInvoicesToExpenses(companyId);
      result.cogsInvoices = cogsResult;
      console.log(`COGS invoices: ${cogsResult.converted} converted, ${cogsResult.skipped} skipped`);
    } catch (error: any) {
      console.error('Error converting COGS invoices:', error);
      result.errors.push(`COGS conversion: ${error.message}`);
    }

    // Step 3: Verify sales transactions exist
    console.log('Step 3: Verifying sales transactions...');
    try {
      const { count, error: salesError } = await supabase
        .from('sales_transactions')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', companyId);

      if (salesError) {
        console.error('Error checking sales transactions:', salesError);
        result.errors.push(`Sales verification: ${salesError.message}`);
      } else {
        result.salesTransactions.count = count || 0;
        console.log(`Found ${result.salesTransactions.count} sales transactions`);
      }
    } catch (error: any) {
      console.error('Error verifying sales transactions:', error);
      result.errors.push(`Sales check: ${error.message}`);
    }

    // Step 4: Update ingredient costs from vendor mappings
    console.log('Step 4: Updating ingredient costs from vendor mappings...');
    try {
      // Get all vendor ingredient mappings with costs
      const { data: mappings, error: mappingsError } = await supabase
        .from('vendor_ingredient_mappings')
        .select('ingredient_id, cost_per_recipe_unit, recipe_unit')
        .eq('company_id', companyId)
        .not('ingredient_id', 'is', null)
        .not('cost_per_recipe_unit', 'is', null);

      if (mappingsError) {
        console.error('Error fetching vendor mappings:', mappingsError);
        result.errors.push(`Mappings fetch: ${mappingsError.message}`);
      } else if (mappings && mappings.length > 0) {
        // Group by ingredient_id and get the most recent/average cost
        const ingredientCosts = new Map<string, { cost: number; unit: string }>();
        
        mappings.forEach((mapping: any) => {
          if (mapping.ingredient_id && mapping.cost_per_recipe_unit) {
            // If already exists, we keep the most recent (last in array)
            ingredientCosts.set(mapping.ingredient_id, {
              cost: mapping.cost_per_recipe_unit,
              unit: mapping.recipe_unit || 'each',
            });
          }
        });

        // Update ingredient prices
        let updatedIngredients = 0;
        for (const [ingredientId, { cost, unit }] of ingredientCosts) {
          const { error: updateError } = await supabase
            .from('ingredients')
            .update({
              cost_per_unit: cost,
              unit_of_measure: unit,
            })
            .eq('id', ingredientId)
            .eq('company_id', companyId);

          if (!updateError) {
            updatedIngredients++;
          }
        }
        console.log(`Updated ${updatedIngredients} ingredient costs from vendor mappings`);
      }
    } catch (error: any) {
      console.error('Error updating ingredient costs:', error);
      result.errors.push(`Ingredient costs: ${error.message}`);
    }

    result.success = result.errors.length === 0;
    return result;
  } catch (error: any) {
    console.error('Error in onboarding data sync:', error);
    result.success = false;
    result.errors.push(`General error: ${error.message}`);
    return result;
  }
}

/**
 * Checks if onboarding data has been synced for a company.
 * Returns true if all necessary data appears to be in place.
 * 
 * @param companyId - The company ID to check
 * @returns True if data appears synced, false otherwise
 */
export async function isOnboardingDataSynced(companyId: string): Promise<boolean> {
  try {
    // Check if there are any bank transactions without a source
    const { count: unsourcedCount } = await supabase
      .from('bank_transactions')
      .select('id', { count: 'exact', head: true })
      .eq('company_id', companyId)
      .is('source', null);

    // Check if there are completed invoices without corresponding expenses
    const { data: invoices } = await supabase
      .from('vendor_invoice_training')
      .select('id')
      .eq('company_id', companyId)
      .eq('ocr_status', 'completed')
      .not('total_amount', 'is', null);

    const invoiceIds = (invoices || []).map(i => i.id);
    
    let unconvertedInvoices = 0;
    if (invoiceIds.length > 0) {
      const { count } = await supabase
        .from('bank_transactions')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', companyId)
        .eq('source', 'vendor_invoice')
        .in('source_reference_id', invoiceIds);
      
      unconvertedInvoices = invoiceIds.length - (count || 0);
    }

    // Data is synced if there are no unsourced transactions and all invoices are converted
    return (unsourcedCount || 0) === 0 && unconvertedInvoices === 0;
  } catch (error) {
    console.error('Error checking onboarding sync status:', error);
    return false;
  }
}

/**
 * Gets a summary of what data needs to be synced for a company.
 * Useful for showing a progress indicator or prompting the user.
 * 
 * @param companyId - The company ID to check
 * @returns Summary of pending sync items
 */
export async function getOnboardingSyncSummary(companyId: string): Promise<{
  bankTransactionsNeedingSource: number;
  invoicesNeedingConversion: number;
  isFullySynced: boolean;
}> {
  try {
    // Count bank transactions without source
    const { count: unsourcedCount } = await supabase
      .from('bank_transactions')
      .select('id', { count: 'exact', head: true })
      .eq('company_id', companyId)
      .is('source', null);

    // Count completed invoices
    const { data: invoices } = await supabase
      .from('vendor_invoice_training')
      .select('id')
      .eq('company_id', companyId)
      .eq('ocr_status', 'completed')
      .not('total_amount', 'is', null);

    const invoiceIds = (invoices || []).map(i => i.id);
    
    let convertedInvoiceCount = 0;
    if (invoiceIds.length > 0) {
      const { count } = await supabase
        .from('bank_transactions')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', companyId)
        .eq('source', 'vendor_invoice')
        .in('source_reference_id', invoiceIds);
      
      convertedInvoiceCount = count || 0;
    }

    const bankTransactionsNeedingSource = unsourcedCount || 0;
    const invoicesNeedingConversion = invoiceIds.length - convertedInvoiceCount;

    return {
      bankTransactionsNeedingSource,
      invoicesNeedingConversion,
      isFullySynced: bankTransactionsNeedingSource === 0 && invoicesNeedingConversion === 0,
    };
  } catch (error) {
    console.error('Error getting sync summary:', error);
    return {
      bankTransactionsNeedingSource: 0,
      invoicesNeedingConversion: 0,
      isFullySynced: false,
    };
  }
}
